/**
 * ModuleContext.tsx
 *
 * Provides a company-wide module enable/disable map to the whole app.
 *
 * How it works:
 *  1. On mount, loads the `module_settings` table for the default company.
 *  2. Exposes `isEnabled(moduleKey)` — used by Sidebar and route guards
 *     to show/hide navigation sections and redirect disabled routes.
 *  3. `toggleModule(key, enabled)` is called from CompanySettingsPage
 *     (super_admin only). It writes to the DB then updates local state.
 *  4. If the table is empty (first run before seeding), all modules default
 *     to `true` so the app never locks itself out.
 *
 * COMPANY_ID is hard-coded to the seed value for Phase 1 (single-tenant).
 * In a multi-tenant Phase 2 this would be read from the auth token or a
 * company selector.
 *
 * The context also exposes the full `Company` record so any page can read
 * company branding, timezone, currency, etc. without an extra query.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useWorkspaceId } from './AuthContext';
import { useBilling } from './BillingContext';
import { ModuleKey, Company } from '../types';
import { fetchModuleAccess, hasViewAccess, AccessLevel, ModuleAccessMap } from '../lib/accessControl';
import { fetchWorkspaceSettings } from '../lib/workspaceSettings';
import { setFormatPreferences } from '../lib/format';

// ─── Constants ────────────────────────────────────────────────────────────────

/** The one real workspace this app was built around. Still the value in production. */
const REAL_SINGLE_WORKSPACE_ID = '00000000-0000-0000-0000-000000000001';

/**
 * An id no workspace has, and none ever will. Used by the tripwire below.
 *
 * Deliberately a VALID uuid, so a query built with it is well-formed and simply matches
 * nothing — a malformed id would make PostgREST error instead, which produces a different and
 * less realistic failure than the one being hunted.
 */
const TRIPWIRE_WORKSPACE_ID = '00000000-0000-0000-0000-0000000000ee';

/**
 * ⚠ NOTHING READS THIS ANY MORE, AND NOTHING SHOULD START. See claude/PLAN_multi_tenancy.md.
 *
 * ── THE BURN-DOWN IS FINISHED: 211 readers → 0, across 69 files ─────────────────────
 * This used to be the answer to "which company is this?" — asserted in 211 places, and
 * correct only because there has only ever been one company. The answer now comes from the
 * signed-in person: `useWorkspaceId()` (React) or `getCurrentWorkspaceId()` (plain modules),
 * both published by AuthContext from `profiles.workspace_id`. Confirm with
 * `node scripts/workspace-burndown.js`; a lint rule warns on any import of this name.
 *
 * ── WHAT THAT MEANS FOR THE TRIPWIRE BELOW ─────────────────────────────────────────
 * The tripwire swapped this constant for an id no workspace has, so any line left behind
 * emptied a screen in front of a developer instead of leaking in front of a customer a year
 * later. It did its job: its last reading found 8 stragglers, and they are all migrated.
 *
 * ARMING IT NOW IS A PROVABLE NO-OP — with zero importers, no code path can observe the
 * value at all. It is kept, unarmed, only so the two-account verification pass has it to
 * hand; once that pass is done, this block and both constants should be DELETED. Until
 * then the lint rule, not the tripwire, is what stops a new reader appearing.
 *
 *     .env.local        →   REACT_APP_WORKSPACE_TRIPWIRE=on
 *     then restart the dev server. Remove the line (or set `off`) to disarm.
 *
 * `NODE_ENV === 'production'` can never arm it, whatever the env var says — a build that
 * shipped with this armed would break the live app for every user.
 *
 * It is opt-in, per developer, and never armed in production, because Lis uses this app
 * daily and finds real bugs by doing so; arming it by default was never worth the price.
 */
export const WORKSPACE_TRIPWIRE_ARMED =
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_WORKSPACE_TRIPWIRE === 'on';

export const DEFAULT_WORKSPACE_ID = WORKSPACE_TRIPWIRE_ARMED
  ? TRIPWIRE_WORKSPACE_ID
  : REAL_SINGLE_WORKSPACE_ID;

/** @deprecated Use DEFAULT_WORKSPACE_ID. Kept for backward compatibility. */
export const DEFAULT_COMPANY_ID = DEFAULT_WORKSPACE_ID;

/* Say so, loudly and once. An armed tripwire empties screens on purpose, and a developer who
   has forgotten they armed it would otherwise spend an hour debugging a working app. */
if (WORKSPACE_TRIPWIRE_ARMED) {
  // eslint-disable-next-line no-console
  console.warn(
    '%c[workspace tripwire ARMED]%c — and since the phase-5 burn-down this changes NOTHING.\n' +
    'Zero modules import DEFAULT_WORKSPACE_ID, so no screen can observe the swapped value. ' +
    'If you armed this expecting empty lists, the expectation is out of date, not the app.\n' +
    'Disarm: remove REACT_APP_WORKSPACE_TRIPWIRE from .env.local and restart the dev server.\n' +
    'Confirm for yourself: node scripts/workspace-burndown.js',
    'font-weight:bold;color:#b45309', 'color:inherit',
  );
}

/** All module keys in their canonical order. Mirrors the keys seeded in
 *  module_settings (8 original + crm/marketing/leave from 0057, + assets from 0059,
 *  + time for the time-tracking widget). */
export const ALL_MODULE_KEYS: ModuleKey[] = [
  'hr',
  'performance',
  'projects',
  'support',
  'communication',
  'finance',
  'analytics',
  'recruitment',
  'crm',
  'marketing',
  'leave',
  'assets',
  'time',
  /* ⛔ NO `mail` HERE EITHER, AND REMOVING IT IS WHY THE COMPANY SWITCH IS GONE.
     This list is what the module map is built FROM, and the map starts from `false` — so
     while `mail` was in it (#562), a workspace with no `module_settings` row read webmail
     as DISABLED. That is exactly what denied `/mail` to the employee in workspace `..0002`.
     Webmail is an App: every employee in every workspace always gets it, so it must not be
     a key the company can switch at all. Lis, 2026-09-09, restated 2026-10-05.
     ⚠️ Migration 0052 seeded a `module_settings` row and 8 `role_module_defaults` rows
     for `mail`. They are LEFT IN PLACE and are now inert — nothing reads them. Lis's
     decision 2026-10-05: no database changes in this PR. */
];

// ─── Context type ─────────────────────────────────────────────────────────────

interface ModuleContextValue {
  /** True while the initial load is in progress. */
  loading: boolean;
  /** The current company profile (null until loaded). */
  company: Company | null;
  /** Map of module_key → enabled boolean. */
  moduleMap: Record<ModuleKey, boolean>;
  /** Returns true if the given module is enabled (defaults to true if not yet loaded). */
  isEnabled: (key: ModuleKey) => boolean;
  /** The signed-in person's per-module access level ('none' | 'view' | 'full'). */
  accessFor: (key: ModuleKey) => AccessLevel;
  /** Company ceiling AND the person's own level: can this person open this module? */
  canAccess: (key: ModuleKey) => boolean;
  /**
   * Toggle a module on or off.
   * Writes to the DB (requires super_admin RLS) then updates local state.
   * Returns true on success.
   */
  toggleModule: (key: ModuleKey, enabled: boolean) => Promise<boolean>;
  /**
   * Update company profile fields.
   * Writes to the DB then refreshes local state.
   */
  updateCompany: (patch: Partial<Company>) => Promise<boolean>;
  /** Force a reload of company + module data (e.g. after setup wizard). */
  reload: () => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ModuleContext = createContext<ModuleContextValue>({
  loading:      true,
  company:      null,
  moduleMap:    {} as Record<ModuleKey, boolean>,
  isEnabled:    () => true,
  accessFor:    () => 'full',
  canAccess:    () => true,
  toggleModule: async () => false,
  updateCompany: async () => false,
  reload:       async () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

/**
 * Wrap the whole app in <ModuleProvider> (inside AuthProvider) so every
 * component can call useModules() without prop-drilling.
 */
export function ModuleProvider({ children }: { children: React.ReactNode }) {
  // The signed-in person's company. This is what makes `useWorkspaceCurrency()` below
  // read the RIGHT workspace's currency rather than company one's.
  const { workspaceId, settled } = useWorkspaceId();
  // The CEILING: what this company actually bought. BillingProvider wraps this one for
  // exactly this reason — see the provider order note in App.tsx.
  const { subscription, isPlanModule } = useBilling();
  const [loading, setLoading]   = useState(true);
  const [company, setCompany]   = useState<Company | null>(null);
  const [moduleMap, setModuleMap] = useState<Record<ModuleKey, boolean>>(
    Object.fromEntries(ALL_MODULE_KEYS.map(k => [k, true])) as Record<ModuleKey, boolean>
  );
  /**
   * The signed-in person's per-module access. Starts as everything = 'full': before
   * migration 0151's RPC exists there IS no per-person layer, so the app must behave
   * exactly as it always has. The RPC result replaces this when it loads (fail closed
   * on a real answer; fail open on an error, same rule as settingsLoaded).
   */
  const [accessMap, setAccessMap] = useState<ModuleAccessMap | null>(null);
  /**
   * Did the module_settings read actually SUCCEED? Not the same as "did it return rows".
   *
   * This one boolean is what lets an empty result be a real answer. Before it, "no rows"
   * and "the query failed" were the same thing and both meant "switch everything on" —
   * which is how a company that bought only CRM would have got the entire product. Now:
   *   false → we do not know; every module stays ON, so nobody is ever locked out of
   *            their own app by a network blip.
   *   true  → the rows ARE the answer, and zero rows means zero modules.
   * Migration 0137 seeds twelve explicit rows per workspace, so "loaded and empty" should
   * only ever be a workspace that predates it.
   */
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  /** Load company profile and module settings from Supabase. */
  const load = useCallback(async () => {
    // Nothing to load until we know whose company this is. Two different situations
    // reach here and they must NOT be collapsed:
    //   still resolving       → stay on the spinner; the effect below re-runs when it lands.
    //   settled, no workspace → genuinely unprovisioned; stop loading and show the defaults.
    if (!workspaceId) {
      if (settled) setLoading(false);
      return;
    }
    try {
      // eslint-disable-next-line no-restricted-syntax -- Promise.all results: error handling verified in surrounding code
      // eslint-disable-next-line no-restricted-syntax -- Promise.all results: error handling verified in surrounding code
      const [compRes, modRes, accessRes, wsSettings] = await Promise.all([
        supabase
          .from('workspaces')
          .select('*')
          .eq('id', workspaceId)
          .single(),
        supabase
          .from('module_settings')
          .select('module_key, enabled')
          .eq('workspace_id', workspaceId),
        fetchModuleAccess(),
        fetchWorkspaceSettings(workspaceId),
      ]);

      if (compRes.data) {
        // The workspace_settings row (0153) wins on currency — it is the row the
        // Settings → CRM → Currency & format editor writes. Overlaying it here
        // means useWorkspaceCurrency() and every `company?.currency` call site
        // (Invoices, Quotes, AR-aging…) pick the setting up with no changes.
        // Null (pre-migration, or no row) = keep workspaces.currency as today.
        const comp = compRes.data as Company;
        setCompany(wsSettings?.currency ? { ...comp, currency: wsSettings.currency } : comp);
      }
      if (wsSettings) {
        // Push the saved date/number formats into the formatting engine once per
        // load; formatDate/formatMoney render in the chosen style app-wide.
        setFormatPreferences({
          dateFormat: wsSettings.date_format,
          numberFormat: wsSettings.number_format ?? null,
        });
      }

      if (accessRes) setAccessMap(accessRes);

      if (modRes.error) {
        // Leave settingsLoaded false: an unreadable table means "unknown", not "none".
        console.warn('ModuleContext: module_settings read failed:', modRes.error.message);
      } else {
        // Start from OFF, not ON. Every module a workspace is entitled to has a row saying
        // so (0137); anything absent is absent on purpose.
        const map = { ...Object.fromEntries(ALL_MODULE_KEYS.map(k => [k, false])) } as Record<ModuleKey, boolean>;
        (modRes.data ?? []).forEach((row: { module_key: string; enabled: boolean }) => {
          if (ALL_MODULE_KEYS.includes(row.module_key as ModuleKey)) {
            map[row.module_key as ModuleKey] = row.enabled;
          }
        });
        setModuleMap(map);
        setSettingsLoaded(true);
      }
    } catch (err) {
      // Network error or unexpected throw — log and continue. settingsLoaded stays false,
      // so every module reads as enabled and the app never locks itself out when the DB
      // is unreachable. Fail OPEN on an error; fail CLOSED only on a real, empty answer.
      console.warn('ModuleContext load failed:', err);
    } finally {
      // Always resolve loading — mirrors the safety-valve pattern in AuthContext.
      setLoading(false);
    }
  }, [workspaceId, settled]);

  useEffect(() => {
    // Safety valve — if load() hangs more than 5 s, force-clear the spinner
    const timeout = setTimeout(() => setLoading(false), 5000);
    load().finally(() => clearTimeout(timeout));
    return () => clearTimeout(timeout);
  }, [load]);

  /**
   * Is this module available right now? TWO questions, ANDed, in this order:
   *
   *   1. THE CEILING — did they pay for it? `subscriptions` is the record of what was
   *      bought. **A workspace with NO subscription row has no ceiling**, rather than a
   *      ceiling of nothing: every workspace that exists today was provisioned by hand and
   *      nothing has ever been sold through Stripe, so reading "no row" as the free plan
   *      would empty the product for everybody. The ceiling starts applying the moment a
   *      real subscription exists, which is the point at which it means something.
   *   2. THE ADMIN'S CHOICE — did they switch it on? This can only narrow the ceiling,
   *      never widen it, which is what stops an admin enabling something unpaid.
   *
   * (Lis's decision, 2026-07-30: "the subscription is the ceiling and the admin chooses
   * within it".)
   */
  const isEnabled = useCallback(
    (key: ModuleKey) => {
      if (subscription && !isPlanModule(key)) return false;
      if (!settingsLoaded) return true;
      return moduleMap[key] === true;
    },
    [moduleMap, settingsLoaded, subscription, isPlanModule]
  );

  /**
   * This person's access level for a module. The effective level comes from the
   * database RPC (company-off wins, then super-admin, then exception, then role
   * default) — this mirrors it, never recomputes it. `null` accessMap means the
   * RPC has not answered yet (or does not exist pre-0151): everything reads
   * 'full', preserving exactly today's behaviour until the layer is live.
   */
  const accessFor = useCallback(
    (key: ModuleKey): AccessLevel => accessMap?.[key] ?? 'full',
    [accessMap]
  );

  /** Company ceiling AND the person's own level. This is what the gates call. */
  const canAccess = useCallback(
    (key: ModuleKey) => isEnabled(key) && hasViewAccess(accessFor(key)),
    [isEnabled, accessFor]
  );

  /**
   * Toggle a module on or off.
   * Uses upsert so it works whether the row exists or not.
   */
  const toggleModule = useCallback(async (key: ModuleKey, enabled: boolean): Promise<boolean> => {
    if (!workspaceId) return false;
    const { error } = await supabase
      .from('module_settings')
      .upsert(
        { workspace_id: workspaceId, module_key: key, enabled, updated_at: new Date().toISOString() },
        { onConflict: 'workspace_id,module_key' }
      );
    if (error) return false;
    setModuleMap(prev => ({ ...prev, [key]: enabled }));
    return true;
  }, [workspaceId]);

  /** Patch the company profile row. */
  const updateCompany = useCallback(async (patch: Partial<Company>): Promise<boolean> => {
    if (!workspaceId) return false;
    const { error } = await supabase
      .from('workspaces')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', workspaceId);
    if (error) return false;
    setCompany(prev => prev ? { ...prev, ...patch } : prev);
    return true;
  }, [workspaceId]);

  return (
    <ModuleContext.Provider value={{ loading, company, moduleMap, isEnabled, accessFor, canAccess, toggleModule, updateCompany, reload: load }}>
      {children}
    </ModuleContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Access module toggle state and company info from any component.
 *
 * @example
 * const { isEnabled } = useModules();
 * if (!isEnabled('finance')) return null;
 */
export function useModules() {
  return useContext(ModuleContext);
}

/**
 * THE workspace currency — the ISO code amounts render in when the record itself
 * carries none.
 *
 * `workspaces.currency` is the app's answer to "what money is this company in?".
 * Migration 0103 set it to EUR after the original seed left it at a generic 'USD',
 * which is why invoices showed "$200.00" and payments "+200,00 €" for the same
 * invoice. It is DATA, not a constant: a future client can run CBOps in GBP or CHF,
 * so nothing hardcodes the symbol (see the header of lib/format.ts).
 *
 * EUR is the fallback for exactly one situation — the context has not loaded yet, or
 * the row carries no currency. It is the house currency, and it matches what
 * `formatMoney` defaults to, so an early render cannot flash a different symbol.
 *
 * WHY A HOOK RATHER THAN `useModules().company?.currency` AT EACH CALL SITE. That
 * exact expression is written out four times already (Invoices, Quotes, AR-aging,
 * recurring invoices), each with its own `|| 'EUR'`. This is the one place it should
 * live. Those four are NOT migrated here — that is a separate pass — but new callers
 * use this and the count stops growing.
 */
export function useWorkspaceCurrency(): string {
  return useContext(ModuleContext).company?.currency || 'EUR';
}

/* LOOKING FOR THE WORKSPACE ITSELF? It is `useWorkspaceId()` in context/AuthContext, not here:
 * the workspace comes from the signed-in profile, and that is where the profile lives. Plain
 * non-React modules use `getCurrentWorkspaceId()` from lib/apiClient. Both read the same value.
 *
 * The currency above now reads the RIGHT workspace: the `.eq('id', …)` lookups in this file
 * take `useWorkspaceId()`, so a second company sees its own currency, not company one's. */
